import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { formatDateTime, isPast, dateParts, timeOnly, relativeFromNow } from '../lib/format';
import { Tag } from '../components/Tag';
import { Avatar } from '../components/Avatar';
import { GuestRow, GuestCard } from '../components/GuestBadge';
import { SignInModal } from '../components/SignInModal';
import { SubscribePrompt } from '../components/SubscribePrompt';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { useIdentity } from '../lib/use-identity';
import { capture } from '../lib/analytics';

function isMp4Url(url: string): boolean {
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.mp4');
  } catch {
    return false;
  }
}

type RegistrationName = {
  firstName: string;
  lastInitial: string;
};

const EMPTY_REGISTRATION_NAME: RegistrationName = {
  firstName: '',
  lastInitial: '',
};

function registrationNameStorageKey(email: string): string {
  return `workshop-registration-name:${email.trim().toLowerCase()}`;
}

function readRegistrationName(email?: string): RegistrationName {
  if (!email) return EMPTY_REGISTRATION_NAME;

  try {
    const stored = window.localStorage.getItem(registrationNameStorageKey(email));
    if (!stored) return EMPTY_REGISTRATION_NAME;

    const parsed = JSON.parse(stored) as Partial<RegistrationName>;
    return {
      firstName: typeof parsed.firstName === 'string' ? parsed.firstName : '',
      lastInitial:
        typeof parsed.lastInitial === 'string'
          ? Array.from(parsed.lastInitial)
              .filter((character) => /\p{L}/u.test(character))
              .slice(0, 1)
              .join('')
              .toUpperCase()
          : '',
    };
  } catch {
    return EMPTY_REGISTRATION_NAME;
  }
}

function writeRegistrationName(email: string, name: RegistrationName): void {
  try {
    window.localStorage.setItem(registrationNameStorageKey(email), JSON.stringify(name));
  } catch {
    // Registration should still work when storage is unavailable.
  }
}

export function WorkshopDetailPage({ adminPreview = false }: { adminPreview?: boolean } = {}) {
  const { slug = '' } = useParams();
  const query = useQuery({
    queryKey: [adminPreview ? 'admin-preview-workshop' : 'workshop', slug],
    queryFn: () =>
      adminPreview ? api.admin.getWorkshopBySlug(slug) : api.getWorkshop(slug),
    enabled: slug.length > 0,
    retry: false,
  });

  if (query.isLoading) {
    return (
      <div className="container-page py-12">
        <div className="h-8 w-1/2 animate-pulse rounded bg-canvas" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="container-page py-16">
        <h1 className="text-2xl font-semibold tracking-tight">Workshop not found</h1>
        <Link to="/" className="mt-4 inline-block text-accent">← Back to workshops</Link>
      </div>
    );
  }

  const w = query.data.workshop;
  const past = w.status === 'completed' || isPast(w.startsAt);

  return (
    <>
      {!adminPreview ? <WorkshopViewAnalytics workshop={w} isPast={past} /> : null}
      {adminPreview ? <PreviewBanner status={w.status} /> : null}
      {past ? (
        <PastWorkshopView w={w} adminPreview={adminPreview} />
      ) : (
        <UpcomingWorkshopView w={w} slug={slug} />
      )}
    </>
  );
}

function WorkshopViewAnalytics({
  workshop,
  isPast: past,
}: {
  workshop: import('@workshop-hst/shared').WorkshopDto;
  isPast: boolean;
}) {
  useEffect(() => {
    capture('workshop_viewed', {
      workshop_id: workshop.id,
      workshop_slug: workshop.slug,
      workshop_status: workshop.status,
      is_past: past,
      has_recording: workshop.recordingAvailable,
    });
  }, [past, workshop.id, workshop.recordingAvailable, workshop.slug, workshop.status]);

  return null;
}

function PreviewBanner({ status }: { status: string }) {
  const isDraft = status === 'draft';
  return (
    <div className="border-b border-amber-300 bg-amber-100/90 text-amber-900">
      <div className="container-page flex items-center justify-between gap-3 py-2 text-xs font-medium">
        <span>
          <strong className="uppercase tracking-wider mr-2">Preview</strong>
          {isDraft
            ? 'This is a draft. Subscribers cannot see this page until you publish.'
            : `Previewing as a subscriber would see this page. Status: ${status}.`}
        </span>
        <Link to="/admin" className="underline hover:no-underline">Back to admin</Link>
      </div>
    </div>
  );
}

function PastWorkshopView({
  w,
  adminPreview,
}: {
  w: import('@workshop-hst/shared').WorkshopDto;
  adminPreview: boolean;
}) {
  const identity = useIdentity();
  const recordingStarted = useRef(false);
  const recordingCompleted = useRef(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const canWatch = adminPreview || identity?.isSubscriber === true;
  const recordingUrlQuery = useQuery({
    queryKey: [adminPreview ? 'admin-recording-url' : 'recording-url', adminPreview ? w.id : w.slug],
    queryFn: () => (adminPreview ? api.admin.recordingUrl(w.id) : api.recordingUrl(w.slug)),
    enabled: canWatch && w.recordingAvailable,
    retry: false,
    staleTime: 10 * 60 * 1000,
  });
  const recordingUrl = recordingUrlQuery.data?.url;
  const hasRecording = w.recordingAvailable;
  const loadingRecording = canWatch && hasRecording && recordingUrlQuery.isLoading;
  return (
    <div className="container-page py-8 lg:py-10">
      <div className="mb-5 flex items-center justify-between">
        <Link to="/" className="text-sm text-ink-muted hover:text-ink">← All workshops</Link>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-canvas px-2.5 py-1 text-[11px] font-semibold text-ink-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-ink-muted" /> Recorded · {relativeFromNow(w.startsAt)}
        </span>
      </div>

      {/* Two render paths for past recordings:
          - R2-mirrored MP4 (new path): native <video> tag, full control, no
            Zoom branding. Identified by .mp4 extension.
          - Zoom share URL (legacy / fallback when mirror failed): iframe, with
            an open-in-new-tab link because some Zoom share URLs set
            X-Frame-Options: deny and silently render blank inside iframes. */}
      {hasRecording && canWatch ? (
        <div>
          <div data-posthog-block className="overflow-hidden rounded-3xl border border-line bg-black shadow-card">
            <div className="aspect-video">
              {loadingRecording ? (
                <div className="flex h-full w-full items-center justify-center text-sm text-white/70">
                  Preparing recording...
                </div>
              ) : recordingUrl && isMp4Url(recordingUrl) ? (
                <video
                  src={recordingUrl}
                  title={`Recording of ${w.title}`}
                  controls
                  playsInline
                  preload="metadata"
                  className="h-full w-full"
                  onPlay={() => {
                    if (recordingStarted.current) return;
                    recordingStarted.current = true;
                    capture('recording_play_started', {
                      workshop_id: w.id,
                      workshop_slug: w.slug,
                      player: 'native_video',
                    });
                  }}
                  onEnded={() => {
                    if (recordingCompleted.current) return;
                    recordingCompleted.current = true;
                    capture('recording_play_completed', {
                      workshop_id: w.id,
                      workshop_slug: w.slug,
                      player: 'native_video',
                    });
                  }}
                />
              ) : recordingUrl ? (
                <iframe
                  src={recordingUrl}
                  title={`Recording of ${w.title}`}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                  onLoad={() =>
                    capture('recording_player_loaded', {
                      workshop_id: w.id,
                      workshop_slug: w.slug,
                      player: 'zoom_embed',
                    })
                  }
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center px-6 text-center text-sm text-white/70">
                  Recording is not available right now.
                </div>
              )}
            </div>
          </div>
          {recordingUrl && !isMp4Url(recordingUrl) ? (
            <p className="mt-3 text-center text-xs text-ink-subtle">
              Player not loading?{' '}
              <a
                href={recordingUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  capture('recording_opened_external', {
                    workshop_id: w.id,
                    workshop_slug: w.slug,
                    player: 'zoom',
                  })
                }
                className="font-medium text-accent hover:underline"
              >
                Open recording in a new tab →
              </a>
            </p>
          ) : null}
        </div>
      ) : hasRecording ? (
        <div className="flex aspect-video flex-col items-center justify-center rounded-3xl border border-line bg-surface px-6 text-center shadow-card">
          <h2 className="text-xl font-semibold tracking-tight">Sign in to watch</h2>
          <p className="mt-2 max-w-md text-sm text-ink-muted">
            Recordings are available for eligible Lenny's Newsletter subscribers.
          </p>
          <button
            type="button"
            onClick={() => {
              capture('recording_access_clicked', {
                workshop_id: w.id,
                workshop_slug: w.slug,
                signed_in: Boolean(identity),
              });
              if (identity) setSubscribeOpen(true);
              else setSignInOpen(true);
            }}
            className="btn-accent mt-5"
          >
            {identity ? 'Check access' : 'Sign in'}
          </button>
        </div>
      ) : (
        <div className="flex aspect-video items-center justify-center rounded-3xl border border-dashed border-line bg-canvas/40 text-sm text-ink-subtle">
          Recording will be available shortly.
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-[1fr,260px]">
        <div className="min-w-0">
          {w.tags[0] ? (
            <div className="mb-3">
              <Tag>{w.tags[0]}</Tag>
            </div>
          ) : null}
          <h1 className="text-3xl font-semibold tracking-tight lg:text-[34px]">{w.title}</h1>
          <p className="mt-2 text-sm text-ink-muted">{formatDateTime(w.startsAt)} · {w.durationMin} min</p>
          {w.description ? (
            <div className="prose prose-sm max-w-none mt-6 whitespace-pre-wrap text-ink leading-relaxed">
              {w.description}
            </div>
          ) : null}
        </div>

        <aside className="lg:pl-6 lg:border-l lg:border-line">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-subtle">Hosted by</h2>
          <div className="mt-3 flex items-center gap-3">
            <Avatar name={w.hostName} url={w.hostHeadshotUrl} size={36} />
            <div className="flex flex-col">
              <span className="text-sm font-semibold">{w.hostName}</span>
              <span className="text-xs text-ink-subtle">Host</span>
            </div>
          </div>
          {w.guest ? (
            <>
              <h2 className="mt-6 text-xs font-semibold uppercase tracking-wider text-ink-subtle">With guest</h2>
              <div className="mt-3">
                <GuestCard guest={w.guest} />
              </div>
            </>
          ) : null}
        </aside>
      </div>

      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      <SubscribePrompt
        open={subscribeOpen}
        identityName={identity?.name ?? null}
        onClose={() => setSubscribeOpen(false)}
      />
    </div>
  );
}

function UpcomingWorkshopView({ w, slug }: { w: import('@workshop-hst/shared').WorkshopDto; slug: string }) {
  const { day, month, weekday } = dateParts(w.startsAt);
  return (
    <div className="container-page py-10 sm:py-14">
      <Link to="/" className="text-sm text-ink-muted hover:text-ink">← All workshops</Link>

      <div className="mt-6 grid gap-10 lg:grid-cols-[1.4fr,1fr]">
        <div className="min-w-0">
          {w.coverImageUrl ? (
            <img
              src={w.coverImageUrl}
              alt=""
              className="aspect-video w-full rounded-3xl object-cover"
            />
          ) : (
            <div className="aspect-video w-full rounded-3xl bg-gradient-to-br from-accent-soft to-canvas" />
          )}

          <div className="mt-8">
            {w.tags[0] ? (
              <div className="mb-3">
                <Tag>{w.tags[0]}</Tag>
              </div>
            ) : null}
            <h1 className="text-3xl font-semibold leading-tight tracking-tight sm:text-[34px]">{w.title}</h1>
            <p className="mt-2 text-sm text-ink-muted">
              {weekday}, {month} {day} · {timeOnly(w.startsAt)} · {w.durationMin} min
            </p>
            <p className="mt-1 text-[11px] text-ink-subtle">
              Shown in your local timezone
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-5">
              <div className="flex items-center gap-2.5">
                <Avatar name={w.hostName} url={w.hostHeadshotUrl} size={32} />
                <div className="flex flex-col leading-tight">
                  <span className="text-sm font-medium">{w.hostName}</span>
                  <span className="text-xs text-ink-subtle">Host</span>
                </div>
              </div>
              {w.guest ? (
                <div className="border-l border-line pl-5">
                  <GuestCard guest={w.guest} />
                </div>
              ) : null}
            </div>

            {w.description ? (
              <div className="prose prose-sm max-w-none mt-8 whitespace-pre-wrap text-ink leading-relaxed">
                {w.description}
              </div>
            ) : null}
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <RsvpCard slug={slug} title={w.title} />
        </aside>
      </div>
    </div>
  );
}

function RsvpCard({ slug, title }: { slug: string; title: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const toast = useToast();
  const [signInOpen, setSignInOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [unregisterConfirmOpen, setUnregisterConfirmOpen] = useState(false);
  const [registrationName, setRegistrationName] = useState<RegistrationName>(() =>
    readRegistrationName(identity?.email),
  );
  const { firstName, lastInitial } = registrationName;

  useEffect(() => {
    setRegistrationName(readRegistrationName(identity?.email));
  }, [identity?.email]);

  const updateRegistrationName = (next: RegistrationName) => {
    setRegistrationName(next);
    if (identity) writeRegistrationName(identity.email, next);
  };

  // Look up the existing registration whenever we have an identity. This is
  // what flips the card from "Register" to "You're in" without forcing a
  // speculative POST that would silently no-op on the backend.
  //
  // Cache key includes isSubscriber because the API gates the join URL on it
  // (lapsed subscriber sees the registration but not the link) — so flipping
  // identity subscription status must invalidate the cached payload.
  const myRegQueryKey = [
    'my-registration',
    slug,
    identity?.email ?? null,
    identity?.isSubscriber ?? null,
  ];
  const myReg = useQuery({
    queryKey: myRegQueryKey,
    queryFn: () => api.myRegistration(slug),
    enabled: Boolean(identity),
    retry: false,
    // The registration state changes only via the buttons on this page; no
    // need to refetch on window focus or interval.
    staleTime: Infinity,
  });

  const register = useMutation({
    mutationFn: (name: { firstName: string; lastInitial: string }) =>
      api.register(slug, name),
    onSuccess: () => {
      capture('workshop_registration_completed', { workshop_slug: slug });
      // Optimistically flip the card; the next render reads from myReg cache.
      qc.setQueryData(myRegQueryKey, {
        registration: { registeredAt: new Date().toISOString(), joinUrl: null },
      });
      // Also refetch to pick up the real joinUrl from the server.
      qc.invalidateQueries({ queryKey: myRegQueryKey });
    },
    onError: (err) => {
      capture('workshop_registration_failed', {
        workshop_slug: slug,
        status_code: err instanceof ApiError ? err.status : null,
      });
      if (err instanceof ApiError) {
        if (err.status === 401) setSignInOpen(true);
        else if (err.status === 403) setSubscribeOpen(true);
      }
    },
  });

  const unregister = useMutation({
    mutationFn: () => api.unregister(slug),
    onSuccess: (result) => {
      capture('workshop_unregistered', { workshop_slug: slug });
      qc.setQueryData(myRegQueryKey, { registration: null });
      qc.invalidateQueries({ queryKey: myRegQueryKey });
      // Be honest about whether the email actually went out — the server
      // catches send failures and reports notificationSent=false instead of
      // erroring the whole call. We don't want a toast that says "check your
      // inbox" when nothing was sent.
      toast.success(
        'Unregistered',
        result.notificationSent
          ? 'Confirmation sent. Your calendar event will be removed shortly.'
          : 'You\'re off the list. We couldn\'t send the email confirmation — remove the calendar event manually.',
      );
    },
    onError: (err) =>
      toast.error('Unregister failed', err instanceof ApiError ? err.message : String(err)),
  });

  const attemptRegister = () => {
    if (!identity) {
      capture('workshop_registration_sign_in_required', { workshop_slug: slug });
      setSignInOpen(true);
      return;
    }
    capture('workshop_registration_started', { workshop_slug: slug });
    register.mutate({
      firstName: firstName.trim(),
      lastInitial: lastInitial.toUpperCase(),
    });
  };

  const isRegistered = Boolean(myReg.data?.registration);

  // Registered state — show confirmation + an unregister escape hatch. Used
  // both for pre-existing registrations (loaded via myReg query) and
  // immediately-after-RSVP (register mutation primes the cache).
  if (identity && isRegistered) {
    return (
      <>
        <div className="rounded-2xl border border-line bg-surface p-6 shadow-card">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-xl text-accent">
            ✓
          </div>
          <h2 className="mt-4 text-xl font-semibold tracking-tight">You're in.</h2>
          <p className="mt-2 text-sm text-ink-muted">
            You're registered with <strong data-private>{identity.email}</strong>. We'll email your calendar
            invite and Zoom link shortly. See you at <strong>{title}</strong>.
          </p>

          <div className="mt-5 border-t border-line pt-4">
            <button
              type="button"
              onClick={() => setUnregisterConfirmOpen(true)}
              disabled={unregister.isPending}
              className="text-xs text-ink-subtle hover:text-ink hover:underline disabled:opacity-50"
            >
              {unregister.isPending ? 'Unregistering…' : "Can't make it? Unregister"}
            </button>
          </div>
        </div>

        <ConfirmDialog
          open={unregisterConfirmOpen}
          title="Unregister from this workshop?"
          body="The event will be removed from your calendar. The shared Zoom link remains active for other attendees, and you can register again later if your plans change."
          confirmLabel="Unregister"
          destructive
          onConfirm={() => {
            setUnregisterConfirmOpen(false);
            unregister.mutate();
          }}
          onCancel={() => setUnregisterConfirmOpen(false)}
        />
      </>
    );
  }

  const errorBlock =
    register.isError && register.error instanceof ApiError && register.error.status >= 500 ? (
      <p className="mt-3 text-sm text-red-600">{register.error.message}</p>
    ) : null;
  return (
    <>
      <div className="rounded-2xl border border-line bg-surface p-6 shadow-card">
        <h2 className="text-xl font-semibold tracking-tight">Register</h2>
        <p className="mt-1 text-sm text-ink-muted">
          We'll email you a calendar invite with the Zoom link.
        </p>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            attemptRegister();
          }}
        >
          {identity ? (
            <>
              <div className="mt-5 rounded-xl border border-line bg-canvas/60 px-3.5 py-3 text-sm">
                <p className="text-xs uppercase tracking-wide text-ink-subtle">Registering with</p>
                <p data-private className="mt-0.5 font-medium">{identity.email}</p>
              </div>

              <div className="mt-4 grid grid-cols-[minmax(0,1fr)_96px] gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-ink-muted">
                    First name
                  </span>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(event) =>
                      updateRegistrationName({
                        firstName: event.target.value,
                        lastInitial,
                      })
                    }
                    autoComplete="given-name"
                    required
                    maxLength={100}
                    className="input w-full"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-ink-muted">
                    Last initial
                  </span>
                  <input
                    type="text"
                    value={lastInitial}
                    onChange={(event) => {
                      const nextLastInitial = Array.from(event.target.value)
                          .filter((character) => /\p{L}/u.test(character))
                          .slice(0, 1)
                          .join('')
                          .toUpperCase();
                      updateRegistrationName({
                        firstName,
                        lastInitial: nextLastInitial,
                      });
                    }}
                    required
                    maxLength={1}
                    pattern="[\p{L}]"
                    className="input w-full uppercase"
                  />
                </label>
              </div>
            </>
          ) : null}

          <button
            type="submit"
            disabled={register.isPending || myReg.isLoading}
            className="btn-accent mt-5 w-full"
          >
            {register.isPending
              ? 'Registering…'
              : myReg.isLoading
                ? 'Checking…'
                : identity
                  ? 'Reserve my spot'
                  : 'Sign in to register'}
          </button>
        </form>

        {!identity ? (
          <p className="mt-3 text-[11px] text-ink-subtle">
            Workshops are for Lenny's Newsletter subscribers.
          </p>
        ) : null}

        {errorBlock}
      </div>

      <SignInModal
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
      />
      <SubscribePrompt
        open={subscribeOpen}
        identityName={firstName.trim() || null}
        onClose={() => {
          setSubscribeOpen(false);
          register.reset();
        }}
      />
    </>
  );
}
